/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  EmptyState,
  AFPTable,
  Pagination,
  Button,
} from '@gsa/afp-component-library';
import { useNavigate } from 'react-router-dom';
import { useEditSolicitation } from '../../components/apis/edit-solicitation-provider';
import useAssignQuantity from './assign-quantity-api';
import './assign-quantity.scss';
import SolicitationStepper from '../../components/solicitation-stepper';
import WelcomeMessage from '@/widgets/welcome-message';
import { CSV_DELIMITER, SOLICITATION_PROCESS } from '../../constants';
import SolicitationStatusBadge from '../../components/solicitation-status-badge';
import InactiveSIAlert from '../../components/solicitation-alert/inactive-si-alert';
import DefaultQuantityAlert from './default-quantity-alert';

const AssignQuantityTable = () => {
  const navigate = useNavigate();

  const { solicitation } = useEditSolicitation();
  const {
    originalLines,
    setOriginalLines,
    solicitationLines,
    setSolicitationLines,
    setSolicitationNotification,
    updateSolicitationLine,
  } = useAssignQuantity(solicitation.id);

  const customSolicitationLinesCSV = {
    header: 'Standard Items,Total Quantity,Fleet Quantity',
  };

  const [paginationState, setPaginationState] = useState({
    limit: 10,
    offset: 0,
    currentPage: 1,
  });
  const [rows, setRows] = useState([]);

  const generateSolicitaionLinesCSV = useCallback(() => {
    const solicitaionLinesCSV = originalLines.map((item) => {
      const escapedTitle = item.standardItem.title
        .replaceAll('"', String.raw`\"`)
        .replaceAll("'", String.raw`\'`);
      return `"${item.standardItem.standardItemNumber}-${escapedTitle}"`
        .toString()
        .concat(CSV_DELIMITER.comma)
        .concat(item.estimatedQty.toString())
        .concat(CSV_DELIMITER.comma)
        .concat(item.estimatedFleetQty)
        .toString();
    });
    if (solicitaionLinesCSV) {
      solicitaionLinesCSV.unshift(customSolicitationLinesCSV.header);
    }
    return solicitaionLinesCSV.join('\r\n');
  }, [originalLines]);

  const downloadSolicitaionLineCSV = () => {
    const csvData = generateSolicitaionLinesCSV();
    // A Blob for having a csv file format
    const blob = new Blob([csvData], { type: 'text/csv' });
    // An object for downloading url
    const url = globalThis.URL.createObjectURL(blob);
    // Creating an anchor(a) tag of HTML
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute(
      'download',
      `${'solicitaion-lines'}-${
        solicitation.solicitationNumber
      }-${Date.now()}.csv`,
    );
    a.click();
  };

  const tableRef = React.createRef();

  useEffect(() => {
    const { limit, offset } = paginationState;
    setRows(solicitationLines.slice(offset, offset + limit));
  }, [solicitationLines, paginationState]);

  const onClickPrev = () => {
    setSolicitationNotification([]);
    navigate(`/catalog/solicitations/edit/${solicitation.id}/select-sin`);
  };

  const navigateNext = () => {
    navigate(`/catalog/solicitations/edit/${solicitation.id}/review`);
  };

  const validateSolicitationLine = (updatedLines) => {
    let isValid = true;
    if (updatedLines.length !== 0) {
      const errorRows = updatedLines.filter((item) => {
        return item.estimatedQty < item.estimatedFleetQty;
      });
      if (errorRows.length > 0) {
        isValid = false;
        setSolicitationNotification([
          {
            id: 'SOLICITATION_LINES_ERROR',
            message: `${errorRows.length} Standard Items have Fleet quantity greater than the Total quantity.`,
            type: 'error',
            closeable: false,
            showInModal: false,
          },
        ]);
      }
    }
    return isValid;
  };

  const getSolLineUpdates = useCallback(() => {
    const lines = [];
    for (let i = 0; i < originalLines.length; i += 1) {
      if (
        originalLines[i].estimatedQty !== solicitationLines[i].estimatedQty ||
        originalLines[i].estimatedFleetQty !==
          solicitationLines[i].estimatedFleetQty
      ) {
        lines.push({
          solicitaionLineID: Number(solicitationLines[i].id),
          estimatedQty: solicitationLines[i].estimatedQty,
          estimatedFleetQty: solicitationLines[i].estimatedFleetQty,
        });
      }
    }
    return lines;
  }, [originalLines, solicitationLines]);

  const onSave = (goNext) => {
    setSolicitationNotification([]);

    const updatedLines = getSolLineUpdates();
    if (validateSolicitationLine(updatedLines)) {
      if (updatedLines.length > 0) {
        updateSolicitationLine({
          variables: { inputs: { solicitationLines: updatedLines } },
          onCompleted: (data) => {
            if (data.updateSolicitationLines) {
              setSolicitationNotification([
                {
                  id: 'UPDATE_SOLICITATION_LINE_SUCCESS',
                  message: `Standard Item quantities have been saved.`,
                  type: 'success',
                  closeable: true,
                  showInModal: false,
                },
              ]);
              if (goNext) {
                navigateNext();
              } else {
                // sync originalLines with current state - no refetch needed
                setOriginalLines([...solicitationLines]);
              }
            }
          },
        });
      } else if (goNext) {
        navigateNext();
      }
    }
  };

  const preventNegativeAndNonNumericInput = (e) => {
    if (!(e.charCode > 47 && e.charCode < 58)) {
      e.preventDefault();
    }
  };

  const preventPaste = (e) => {
    e.preventDefault();
  };

  const onBlurHandler = (inputName, original, e) => {
    if (e.target.value === '0' || e.target.value === '') {
      e.target.value = '1';
    }

    setSolicitationLines((prevLines) => {
      const updatedLines = prevLines.map((line) => {
        if (line.id === original.id) {
          return {
            ...line,
            [inputName]: Number(e.target.value),
          };
        }
        return line;
      });
      return updatedLines;
    });
  };

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: 'Standard Item',
        accessor: 'standardItem',
        Cell: ({ value }) => {
          return `${value?.standardItemNumber}-${value?.title}`;
        },
        sortable: false,
      },
      {
        Header: 'Total quantity',
        accessor: 'estimatedQty',
        sortable: false,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row: { original }, value }) => (
          <div key={original.id} className="usa-form margin-top-neg-2">
            <input
              data-testid="total-quantity"
              className="assign-qty-input"
              min={1}
              maxLength={6}
              type="text"
              defaultValue={value}
              onKeyPress={preventNegativeAndNonNumericInput}
              onPaste={preventPaste}
              onBlur={(event) => {
                onBlurHandler('estimatedQty', original, event);
              }}
            />
          </div>
        ),
      },
      {
        Header: 'Fleet quantity',
        accessor: 'estimatedFleetQty',
        sortable: false,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row: { original }, value }) => (
          <div key={original.id} className="usa-form margin-top-neg-2">
            <input
              data-testid="fleet-quantity"
              className="assign-qty-input"
              name="estimatedFleetQty"
              type="text"
              maxLength={6}
              min={1}
              defaultValue={value}
              required
              onKeyPress={preventNegativeAndNonNumericInput}
              onPaste={preventPaste}
              onBlur={(event) => {
                onBlurHandler('estimatedFleetQty', original, event);
              }}
            />
          </div>
        ),
      },
    ];
    return columnList;
  }, []);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    const offset = (currentPage - 1) * itemsPerPage;
    setPaginationState({
      limit: itemsPerPage,
      offset,
      currentPage,
    });
  };

  const contYear = solicitation.contractYear;

  return (
    <>
      <InactiveSIAlert
        solicitationPrograms={solicitation.programs}
        contractYear={solicitation.contractYear}
      />
      <DefaultQuantityAlert originalLines={originalLines} />

      <div className="grid-row flex-row assign-quantity-header">
        <h1>
          {`${solicitation.solicitationType} Solicitation - ${solicitation.solicitationNumber}`}
        </h1>
        <div className="statusBadgeContainer margin-left-1">
          <SolicitationStatusBadge status={solicitation.status} />
        </div>
      </div>
      <SolicitationStepper
        currentStep={solicitation.bidType === 'IN_FLEET' ? 5 : 3}
        solicitation={solicitation}
      />
      <WelcomeMessage message="Enter quantities for all your Standard Items. Fleet quantity represents vehicles for lease, while Total quantity represents the total vehicle count for purchase and lease from vendors." />

      <div className="padding-top-2 display-flex flex-row flex-justify-start">
        <span
          className="text-bold"
          aria-label="Contract Year"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex="0"
        >
          Contract year
        </span>
        <span
          className="padding-left-8"
          aria-label={contYear}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex="0"
        >
          {contYear || '----'}
        </span>
      </div>

      <div className="grid-col-10 display-flex flex-row flex-justify-end">
        {originalLines.length > 0 ? (
          <Button
            variant="outline"
            label="Export CSV"
            leftIcon={{ name: 'file_download' }}
            onClick={downloadSolicitaionLineCSV}
          />
        ) : null}
      </div>
      <div className="grid-col-10">
        <div id="assign-quantity-table">
          <AFPTable
            testId="assign-quantity-table"
            columns={columns}
            data={rows}
            fullWidth
            ref={tableRef}
          />
        </div>
        <div className="padding-y-1 pagination">
          <Pagination
            itemsPerPageOptions={[10, 25, 50]}
            onPageChange={handlePaginationChange}
            variant="advanced"
            itemsPerPage={paginationState.limit}
            currentPage={paginationState.currentPage}
            itemsCount={solicitationLines.length}
          />
        </div>
      </div>

      {solicitationLines.length === 0 && (
        <div
          className="margin-top-neg-5 grid-col-10"
          id="assign-quantity-table-empty-state"
        >
          <EmptyState
            hasBackground
            containerStyles="padding-y-8 margin-top-1 height-full"
            topText={
              solicitation.solicitationType === SOLICITATION_PROCESS.RENEWAL
                ? 'There are no Standard Items for the contract year.'
                : 'There are no Standard Items associated with this solicitation.'
            }
            topTextStyles="margin-top-7"
          />
        </div>
      )}

      <div className="grid-row padding-top-4 padding-bottom-10">
        <Button
          variant="outline"
          label="Previous"
          leftIcon={{ name: 'arrow_back' }}
          data-testid="assign-quantity-prev-button"
          onClick={onClickPrev}
        />
        <Button
          variant="primary"
          label="Save"
          data-testid="assign-quantity-save-button"
          className="margin-left-1"
          onClick={() => onSave(false)}
        />
        <Button
          variant="primary"
          label="Save and continue"
          data-testid="assign-quantity-save-continue-button"
          className="margin-left-1"
          rightIcon={{
            name: 'arrow_forward',
          }}
          onClick={() => onSave(true)}
        />
      </div>
    </>
  );
};

export default AssignQuantityTable;
